import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SelectPicker } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterRequest, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import { AdvanceSharePack, AdvanceSharePackPaginationFilterRequest, AdvanceSharePackStatusList } from '@/models/advance-share-pack.model';
import { getPagedAdvanceSharePacks } from '@/services/advance-share-pack.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const AdvanceSharePackList = () => {
  const [filterReq, setFilterReq] = useState<AdvanceSharePackPaginationFilterRequest>(new PaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<AdvanceSharePack[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: AdvanceSharePackPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<AdvanceSharePack> = await getPagedAdvanceSharePacks(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({
          ...pre,
          pageNumber: result.pageNumber,
          totalRecords: result.totalRecords,
          pageSize: result.pageSize
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching advance share packs:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre,
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/advance-shares/packs/new`)}>
          Add New Pack
        </Button>

        <Stack spacing={6}>
          <SelectPicker
            label="Status"
            data={AdvanceSharePackStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, status: dataKey ?? undefined }))}
          />
          <Input
            placeholder="Keyword"
            value={filterReq.keyword ?? ''}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Cover Image</HeaderCell>
          <ImageCell dataKey="coverUrl" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Title</HeaderCell>
          <Cell dataKey="title" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Status</HeaderCell>
          <Cell>
            {(rowData: AdvanceSharePack) => ( AdvanceSharePackStatusList.findLast(s => s.value == rowData.status)?.label )}
          </Cell>
        </Column>
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData : AdvanceSharePack) => (
              <IconButton onClick={() => nav(`/advance-shares/packs/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />

    </PageContent>
  );
};

export default AdvanceSharePackList;
